import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom-v6'
import {
  Badge,
  Button,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Input,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@clouway/ui'
import { KeyRound, Pencil, UserPlus } from 'lucide-react'

import { Permission, hasPermission, useSession } from '@/features/auth'
import { useDebounce } from '@/hooks/useDebounce'
import { useUsers } from '../hooks'

// The IDP list endpoint returns a bare array with no total count, so paging
// requests one row past the page size and uses its presence to light up Next.
const PAGE_SIZE = 10

export function UsersList() {
  const { t } = useTranslation('feature')
  const { access } = useSession()
  const canWrite = hasPermission(access, Permission.users.create)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const [showDeactivated, setShowDeactivated] = useState(false)
  const debounced = useDebounce(query, 300)

  // A new search or a flip of the deactivated filter invalidates whatever
  // page the user was on.
  useEffect(() => {
    setPage(0)
  }, [debounced, showDeactivated])

  const { data: users, isLoading } = useUsers({
    ...(debounced ? { query: debounced } : {}),
    ...(showDeactivated ? { active: false } : {}),
    limit: PAGE_SIZE + 1,
    offset: page * PAGE_SIZE,
  })
  const rows = users?.slice(0, PAGE_SIZE) ?? []
  const hasNext = (users?.length ?? 0) > PAGE_SIZE

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-4">
          <Input
            className="max-w-xs"
            placeholder={t('user.list.search')}
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
          <div className="flex items-center gap-2">
            <Switch
              id="show-deactivated"
              checked={showDeactivated}
              onCheckedChange={setShowDeactivated}
              aria-label={t('user.list.showDeactivated')}
            />
            <label htmlFor="show-deactivated" className="text-sm text-muted-foreground">
              {t('user.list.showDeactivated')}
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button asChild variant="outline">
              <Link to="/users/new">
                <KeyRound className="size-4" />
                {t('user.list.register')}
              </Link>
            </Button>
          )}
          {canWrite && (
            <Button asChild>
              <Link to="/users/invite">
                <UserPlus className="size-4" />
                {t('user.list.invite')}
              </Link>
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('user.list.empty')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('user.list.name')}</TableHead>
              <TableHead>{t('user.list.email')}</TableHead>
              <TableHead>{t('user.list.active')}</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(user => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.fullName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant={user.active ? 'secondary' : 'outline'}>
                    {t(user.active ? 'user.list.active' : 'user.list.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button asChild size="icon" variant="ghost" aria-label="edit">
                    <Link to={`/users/${user.id}/edit`}>
                      <Pencil className="size-4" />
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!isLoading && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">
            {t('user.list.page', { page: page + 1 })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(current => current - 1)}
              disabled={page === 0}
            >
              {t('user.list.prev')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(current => current + 1)}
              disabled={!hasNext}
            >
              {t('user.list.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
